import { Component, forwardRef, inject, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormArray,
  FormControl,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { finalize, Subject, takeUntil } from 'rxjs';
import { MaterialModule } from '../../../../material.module';
import {
  WebinarAdditionalProperties,
  WebinarPropertyAction,
  WebinarPropertyBinding,
  WebinarPropertyContext,
  WebinarPropertyRow,
} from '../../../../models/webinar.model';
import { WebinarsService } from '../../../../services/webinars/webinars.service';

type PropertyRow = FormGroup<{ propertyName: FormControl<string>; value: FormControl<string> }>;
type ObjectType = 'contacts' | 'leads';

@Component({
  selector: 'app-webinar-additional-properties',
  standalone: true,
  imports: [MaterialModule, ReactiveFormsModule],
  templateUrl: './additional-properties.component.html',
  styleUrls: ['./additional-properties.component.scss'],
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => WebinarAdditionalPropertiesComponent), multi: true },
    { provide: NG_VALIDATORS, useExisting: forwardRef(() => WebinarAdditionalPropertiesComponent), multi: true },
  ],
})
export class WebinarAdditionalPropertiesComponent implements ControlValueAccessor, Validator, OnInit, OnDestroy {
  private readonly service = inject(WebinarsService);
  private readonly destroy$ = new Subject<void>();
  readonly actions: WebinarPropertyAction[] = ['contactCreate', 'leadCreate', 'registrationContactCreate'];
  readonly labels: Record<WebinarPropertyAction, string> = {
    contactCreate: 'Create contact – poll',
    leadCreate: 'Create lead – poll',
    registrationContactCreate: 'Create contact – registration / attendance',
  };
  readonly form = new FormGroup({
    contactCreate: new FormArray<PropertyRow>([]),
    leadCreate: new FormArray<PropertyRow>([]),
    registrationContactCreate: new FormArray<PropertyRow>([]),
  });
  selectedAction: WebinarPropertyAction = 'contactCreate';
  disabled = false;
  readonly contexts: Partial<Record<ObjectType, WebinarPropertyContext>> = {};
  readonly loading: Partial<Record<ObjectType, boolean>> = {};
  readonly errors: Partial<Record<ObjectType, boolean>> = {};
  private binding: WebinarPropertyBinding | null = null;
  private edited = false;
  private initialized = false;
  private malformed = false;
  private onChange: (value: WebinarAdditionalProperties | null) => void = () => {};
  private onTouched = () => {};
  private validationChanged = () => {};

  constructor() {
    this.form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.emitValue());
  }
  ngOnInit(): void {
    this.initialized = true;
    if (!this.disabled) this.loadNeededContexts();
  }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
  objectType(action = this.selectedAction): ObjectType {
    return action === 'leadCreate' ? 'leads' : 'contacts';
  }
  get context(): WebinarPropertyContext | undefined {
    return this.contexts[this.objectType()];
  }
  rows(action = this.selectedAction): FormArray<PropertyRow> {
    return this.form.controls[action];
  }
  get connectionChanged(): boolean {
    return !!this.binding && Object.values(this.contexts).some((context) => !this.sameBinding(this.binding!, context));
  }
  private sameBinding(a: WebinarPropertyBinding, b: WebinarPropertyBinding): boolean {
    return a.clientId === b.clientId && a.portalId === b.portalId && a.connectionId === b.connectionId;
  }
  selectAction(action: WebinarPropertyAction): void {
    this.selectedAction = action;
    if (!this.context) this.loadContext();
  }
  loadContext(objectType = this.objectType()): void {
    if (this.disabled || this.loading[objectType]) return;
    this.loading[objectType] = true;
    this.errors[objectType] = false;
    this.service
      .getHubspotWebinarPropertyContext(objectType)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.loading[objectType] = false;
          this.validationChanged();
        }),
      )
      .subscribe({
        next: (context) => {
          this.contexts[objectType] = context;
          this.validationChanged();
        },
        error: () => {
          this.errors[objectType] = true;
          this.validationChanged();
        },
      });
  }
  private loadNeededContexts(): void {
    for (const objectType of new Set([
      this.objectType(),
      ...this.actions.filter((action) => this.rows(action).length).map((action) => this.objectType(action)),
    ])) {
      if (!this.contexts[objectType]) this.loadContext(objectType);
    }
  }
  property(name: string, action = this.selectedAction) {
    return this.contexts[this.objectType(action)]?.properties.find((property) => property.name === name);
  }
  suggestions(value: string) {
    const query = value.toLowerCase();
    return (this.context?.properties || [])
      .filter((property) => property.name.toLowerCase().includes(query) || property.label.toLowerCase().includes(query))
      .slice(0, 100);
  }
  useCurrentConnection(): void {
    if (!this.context) return;
    this.binding = {
      clientId: this.context.clientId,
      connectionId: this.context.connectionId,
      portalId: this.context.portalId,
    };
    for (const objectType of ['contacts', 'leads'] as const) {
      const context = this.contexts[objectType];
      if (context && !this.sameBinding(this.binding, context)) delete this.contexts[objectType];
    }
    this.loadNeededContexts();
    this.malformed = false;
    this.emitValue();
  }
  add(): void {
    if (!this.disabled && this.context && this.rows().length < 50)
      this.rows().push(this.makeRow({ propertyName: '', value: '' }));
  }
  remove(index: number): void {
    if (!this.disabled) this.rows().removeAt(index);
  }
  private makeRow(row: WebinarPropertyRow): PropertyRow {
    return new FormGroup({
      propertyName: new FormControl(row.propertyName, { nonNullable: true }),
      value: new FormControl(row.value, { nonNullable: true }),
    });
  }
  writeValue(value: WebinarAdditionalProperties | null | undefined): void {
    this.edited = false;
    this.malformed = !!value && this.actions.some((action) => !Array.isArray(value.actions?.[action]));
    this.binding = value
      ? { clientId: value.clientId, portalId: value.portalId, connectionId: value.connectionId }
      : null;
    for (const action of this.actions) {
      this.rows(action).clear({ emitEvent: false });
      if (!this.malformed)
        for (const row of value?.actions[action] || []) this.rows(action).push(this.makeRow(row), { emitEvent: false });
    }
    if (this.initialized && !this.disabled) this.loadNeededContexts();
    this.validationChanged();
  }
  private emitValue(): void {
    this.edited = true;
    const hasRows = this.actions.some((action) => this.rows(action).length);
    if (hasRows && !this.binding && this.context) {
      this.binding = {
        clientId: this.context.clientId,
        portalId: this.context.portalId,
        connectionId: this.context.connectionId,
      };
    }
    const binding = this.binding || this.context || { clientId: '', portalId: '', connectionId: '' };
    this.onChange(
      hasRows
        ? {
            clientId: binding.clientId,
            portalId: binding.portalId,
            connectionId: binding.connectionId,
            actions: this.form.getRawValue(),
          }
        : null,
    );
    this.onTouched();
    this.validationChanged();
  }
  registerOnChange(fn: (value: WebinarAdditionalProperties | null) => void): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
  registerOnValidatorChange(fn: () => void): void {
    this.validationChanged = fn;
  }
  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
    if (disabled) this.form.disable({ emitEvent: false });
    else {
      this.form.enable({ emitEvent: false });
      if (this.initialized) this.loadNeededContexts();
    }
  }
  rowError(action: WebinarPropertyAction, index: number): string | null {
    const row = this.rows(action).at(index).getRawValue();
    if (
      !/^[A-Za-z][A-Za-z0-9_]{0,127}$/.test(row.propertyName) ||
      ['constructor', 'prototype', '__proto__'].includes(row.propertyName)
    )
      return 'Enter a HubSpot internal property name.';
    if (
      this.rows(action)
        .getRawValue()
        .filter((other) => other.propertyName === row.propertyName).length > 1
    )
      return 'This property is already configured for this action.';
    const property = this.property(row.propertyName, action);
    if (!property) return 'Choose a writable property for this object and HubSpot connection.';
    if (!row.value.trim() || row.value.length > 4000) return 'Enter a value of up to 4,000 characters.';
    if (
      property.type === 'number' &&
      (!/^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:e[+-]?\d+)?$/i.test(row.value) || !Number.isFinite(Number(row.value)))
    )
      return 'Enter a finite number, including 0.';
    if (property.type === 'bool' && !['true', 'false'].includes(row.value)) return 'Select true or false.';
    if (
      property.type === 'enumeration' &&
      !property.options?.some((option) => !option.hidden && option.value === row.value)
    )
      return 'Select an internal option value.';
    return null;
  }
  validate(_control: AbstractControl): ValidationErrors | null {
    if (!this.edited || !this.actions.some((action) => this.rows(action).length)) return null;
    return this.malformed ||
      this.connectionChanged ||
      this.actions.some(
        (action) =>
          this.rows(action).length &&
          (!this.contexts[this.objectType(action)] ||
            this.loading[this.objectType(action)] ||
            this.errors[this.objectType(action)] ||
            this.rows(action).length > 50 ||
            this.rows(action).controls.some((_, index) => this.rowError(action, index))),
      )
      ? { webinarAdditionalProperties: true }
      : null;
  }
}
